import { useLazyQuery } from '@apollo/client'
import { GetImportProjectProductFamilyStatusQuery } from 'api/graphQL/queries/project.queries'
import {
    ExecuteProjectImportResult,
    GetBackendProcessStatusInput,
    GetImportProjectProductFamilyStatusResponse,
} from 'api/graphQL/types/backendProcess.types'
import { useCallback, useEffect, useRef } from 'react'

const STATUS_IN_PROGRESS = 'INPROGRESS'
const STATUS_COMPLETED = 'COMPLETE'
const STATUS_ERROR = 'ERROR'
const STATUS_QUEUED = 'QUEUED'

interface UseImportStatusPollerProps {
    instanceId: string | undefined
    isImportInProgress: boolean
    onComplete: (output: ExecuteProjectImportResult) => void
    onError: (errorMessage: string | null | undefined) => void
    onProgress?: (progressPercentage: number, errorPercentage?: number) => void
}

export const useImportStatusPoller = ({
    instanceId,
    isImportInProgress,
    onComplete,
    onError,
    onProgress,
}: UseImportStatusPollerProps) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const isMountedRef = useRef(true)

    useEffect(() => {
        return () => {
            isMountedRef.current = false
        }
    }, [])

    const onStatusQueryCompleted = useCallback(
        (data: GetImportProjectProductFamilyStatusResponse) => {
            const importStatus = data?.getImportProjectProductFamilyStatus
            if (!importStatus) {
                onError('No import status received')
                return
            }

            const state = importStatus.state?.toUpperCase()

            // Report progress if callback is provided
            if (onProgress) {
                onProgress(
                    importStatus.progressPercentage,
                    importStatus.errorPercentage
                )
            }

            switch (state) {
                case STATUS_QUEUED:
                case STATUS_IN_PROGRESS:
                    // Still in progress; poll again after a short time
                    if (isMountedRef.current) {
                        timeoutRef.current = setTimeout(
                            () => queryForStatus(),
                            5000
                        )
                    }
                    break
                case STATUS_COMPLETED:
                    if (importStatus.output) {
                        onComplete(importStatus.output)
                    } else {
                        onError('Import completed but no output received')
                    }
                    break
                case STATUS_ERROR:
                    onError(
                        importStatus.errorMessage ||
                            importStatus.output?.errorMessage ||
                            'Import failed with unknown error'
                    )
                    break
                default:
                    // Unknown status, stop polling
                    onError(`Unknown import status: ${state}`)
                    break
            }
        },
        [onComplete, onError, onProgress]
    )

    const [fetchImportStatus] = useLazyQuery<
        GetImportProjectProductFamilyStatusResponse,
        { input: GetBackendProcessStatusInput }
    >(GetImportProjectProductFamilyStatusQuery, {
        onCompleted: onStatusQueryCompleted,
        onError: (error) =>
            onError(`Failed to fetch import status: ${error.message}`),
        fetchPolicy: 'network-only',
    })

    const queryForStatus = useCallback(() => {
        if (instanceId && isMountedRef.current) {
            fetchImportStatus({
                variables: {
                    input: {
                        instanceId,
                    },
                },
            })
        }
    }, [instanceId, fetchImportStatus])

    useEffect(() => {
        if (instanceId && isImportInProgress && isMountedRef.current) {
            queryForStatus()
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [instanceId, isImportInProgress, queryForStatus])

    return {
        queryForStatus,
    }
}
